import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import { useLanguage } from '../../i18n/LanguageContext';
import { publishedArticles } from './Knowledge.tsx';
import { allModels } from './ProductPage.tsx';
import {
  pageStyle, containerStyle, h1Style, h2Style, bodyTextStyle,
  cardStyle, ctaButtonStyle, bulletDot,
  breadcrumbStyle, breadcrumbListStyle, breadcrumbLinkStyle
} from './pageStyles';

const BASE_URL = 'https://www.hestiadoors.com';

const ArticlePage = () => {
  const { slug } = useParams();
  const { t, pick, path, lang } = useLanguage();
  const article = publishedArticles.find((a: any) => a.slug === slug);

  if (!article) {
    return (
      <div style={pageStyle}>
        <SEO title={`${t('productPage.notFound')} | Hestia Doors`} description={t('productPage.notFoundDesc')} noindex />
        <Navigation />
        <div style={{ ...containerStyle, textAlign: 'center' as const, padding: '6rem 1.5rem' }}>
          <h1 style={h1Style}>{t('productPage.notFound')}</h1>
          <Link to={path('/knowledge')} style={{ color: '#dfa926', fontWeight: 600 }}>{t('knowledge.backTo')}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = pick(article, 'title');
  const description = pick(article, 'description');
  const relatedProducts = (article.productSlugs || [])
    .map((s: string) => allModels.find((e: any) => e.model.slug === s))
    .filter(Boolean);
  const relatedArticles = publishedArticles.filter((a: any) => a.slug !== slug);
  const canonical = `${BASE_URL}${lang === 'et' ? '/et' : ''}/knowledge/${slug}/`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: title,
        description,
        inLanguage: lang,
        datePublished: article.datePublished,
        dateModified: article.dateUpdated,
        author: { '@type': 'Organization', name: 'Hestia Doors', url: BASE_URL },
        publisher: { '@type': 'Organization', name: 'Hestia Doors', url: BASE_URL },
        mainEntityOfPage: canonical
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('productPage.breadcrumbHome'), item: `${BASE_URL}${lang === 'et' ? '/et' : '/'}` },
          { '@type': 'ListItem', position: 2, name: t('knowledge.title'), item: `${BASE_URL}${lang === 'et' ? '/et' : ''}/knowledge/` },
          { '@type': 'ListItem', position: 3, name: title }
        ]
      }
    ]
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={`${title} | Hestia Doors`}
        description={description}
        canonicalPath={`/knowledge/${slug}`}
        jsonLd={jsonLd}
        ogType="article"
      />
      <Navigation />

      <div style={containerStyle}>
        <nav aria-label="Breadcrumb" style={breadcrumbStyle}>
          <ol style={breadcrumbListStyle}>
            <li><Link to={path('/')} style={breadcrumbLinkStyle}>{t('productPage.breadcrumbHome')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li><Link to={path('/knowledge')} style={breadcrumbLinkStyle}>{t('knowledge.title')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li style={{ color: '#dfa926' }} aria-current="page">{title}</li>
          </ol>
        </nav>

        <article style={{ maxWidth: '48rem' }}>
          <h1 style={{ ...h1Style, fontSize: '2.5rem' }}>{title}</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '2rem' }}>
            Hestia Doors · {t('knowledge.updated')} {article.dateUpdated}
          </p>

          {article.sections.map((sec: any, i: number) => (
            <section key={i} style={{ marginBottom: '2.5rem' }}>
              <h2 style={{ ...h2Style, color: 'white' }}>{pick(sec, 'heading')}</h2>
              {(pick(sec, 'paragraphs') as string[] || []).map((p: string, j: number) => (
                <p key={j} style={{ ...bodyTextStyle, marginBottom: '1rem' }}>{p}</p>
              ))}
              {(pick(sec, 'list') as string[] || []).length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {(pick(sec, 'list') as string[]).map((item: string, j: number) => (
                    <li key={j} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.6rem', ...bodyTextStyle }}>
                      <span style={bulletDot} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {article.categoryPaths && article.categoryPaths.length > 0 && (
            <section style={{ marginBottom: '2.5rem' }}>
              <h2 style={h2Style}>{t('knowledge.relatedArticles')}</h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {article.categoryPaths.map((p: string) => (
                  <li key={p} style={{ marginBottom: '0.5rem' }}>
                    <Link to={path(p)} style={{ color: '#dfa926' }}>{t(`categories.${categoryKey(p)}.h1`)}</Link>
                  </li>
                ))}
                {relatedArticles.map((a: any) => (
                  <li key={a.slug} style={{ marginBottom: '0.5rem' }}>
                    <Link to={path(`/knowledge/${a.slug}`)} style={{ color: '#dfa926' }}>{pick(a, 'title')}</Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>

        {relatedProducts.length > 0 && (
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={h2Style}>{t('knowledge.relatedProducts')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
              {relatedProducts.map((e: any) => (
                <Link key={e.model.slug} to={path(`/products/${e.model.slug}`)} style={{ ...cardStyle, padding: '1rem' }}>
                  <img src={e.model.image} alt={pick(e.model, 'name')} loading="lazy"
                    style={{ width: '100%', height: '120px', objectFit: 'cover' as const, borderRadius: '0.375rem', marginBottom: '0.75rem', background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)' }} />
                  <span style={{ color: '#dfa926', fontWeight: 600, fontSize: '0.9rem' }}>{pick(e.model, 'name')}</span>
                  <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>{pick(e.group, 'name')}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div style={{ textAlign: 'center' as const, padding: '2rem 0' }}>
          <p style={bodyTextStyle}>{t('knowledge.ctaTitle')}</p>
          <Link to={path('/contact')} style={ctaButtonStyle}>{t('knowledge.cta')}</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Maps a landing-page path to its categories.* translation key.
const categoryKey = (p: string) => ({
  '/outward-opening-exterior-doors': 'outwardDoors',
  '/inward-opening-exterior-doors': 'inwardDoors',
  '/custom-exterior-doors': 'customDoors',
  '/exterior-doors-for-prefabricated-houses': 'prefabDoors',
  '/exterior-doors': 'exteriorDoors',
  '/storage-doors': 'storageDoors'
}[p] || 'exteriorDoors');

export default ArticlePage;
